import React from 'react';
import { Box, Container, Typography, Link, List, ListItem, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Helmet } from 'react-helmet-async';
import { colors } from '../styles';

/**
 * Public terms for the $10 feedback-call gift card offered in the October 2026
 * win-back email. Offering entity is Budgit. Legal's lines: Monday, Oct 26
 * through Friday, Oct 30, 9 AM to 5 PM CT, completed by Oct 30 at 11:59 PM CT,
 * first 25 completed calls. The cap sentence lives only on
 * REWARD_TERMS.rewardCap so it can be swapped in one place.
 * Layout follows ContestRules.js.
 */

export const REWARD_TERMS = Object.freeze({
  lastUpdated: 'October 20, 2026',
  dates: 'Pick a 10-minute time Monday, Oct 26 through Friday, Oct 30, between 9 AM and 5 PM CT.',
  completeBy: 'Calls must be completed by Oct 30 at 11:59 PM CT.',
  quoteDisclosure: '(received a $10 gift card for a feedback call)',
  rewardCap: "Gift cards go to the first 25 people who complete a call; booking alone doesn't qualify.",
});

const StyledSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '20px',
  backgroundColor: colors.white,
  marginBottom: theme.spacing(3),
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
}));

const Section = ({ id, title, children }) => (
  <StyledSection id={id} component="section" aria-labelledby={`${id}-title`}>
    <Typography id={`${id}-title`} variant="h5" sx={{ fontWeight: 800, color: colors.primary, mb: 1 }}>
      {title}
    </Typography>
    <Box sx={{ color: colors.secondary }}>{children}</Box>
  </StyledSection>
);

const Body = ({ children }) => (
  <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
    {children}
  </Typography>
);

const Bullet = ({ children }) => (
  <ListItem sx={{ display: 'list-item', pl: 0 }}>
    <Typography variant="body1">{children}</Typography>
  </ListItem>
);

const FeedbackRewardTerms = () => (
  <Box sx={{
    backgroundColor: colors.background,
    minHeight: '100vh',
    py: { xs: 6, md: 10 },
    overflowY: 'auto',
    height: '100vh',
    position: 'fixed',
    width: '100%'
  }}>
    <Helmet>
      <title>Feedback Reward Terms · Budgit</title>
    </Helmet>
    <Container maxWidth="md">
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" sx={{ fontWeight: 900, color: colors.primary, mb: 1 }}>
          Feedback Reward Terms
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, color: colors.primary, mb: 1 }}>
          $10 gift card for a 10-minute feedback call
        </Typography>
        <Typography variant="body2" sx={{ color: colors.secondary, opacity: 0.8 }}>
          Last updated: {REWARD_TERMS.lastUpdated}
        </Typography>
      </Box>

      <Section id="offer" title="The offer">
        <Body>
          If you book a 10-minute feedback call with Budgit&apos;s founder through the link in our
          October 2026 email, attend the full 10 minutes, and share feedback about Budgit, we&apos;ll
          send you one $10 digital gift card delivered through Tremendous (you choose the card).
          No purchase or subscription is required.
        </Body>
      </Section>

      <Section id="eligibility" title="Who's eligible">
        <Body>
          US residents 18 or older who received the October 2026 email at the address on their
          Budgit account. People who work for or with Budgit, and members of their households,
          aren&apos;t eligible.
        </Body>
      </Section>

      <Section id="limit" title="Limit">
        <Body>One reward per person and per Budgit account.</Body>
        <Body>{REWARD_TERMS.rewardCap}</Body>
      </Section>

      <Section id="what-counts" title="What counts">
        <List sx={{ listStyleType: 'disc', pl: 3 }}>
          <Bullet>{REWARD_TERMS.dates}</Bullet>
          <Bullet>{REWARD_TERMS.completeBy}</Bullet>
          <Bullet>
            Join the call, stay for the full 10 minutes, and share your feedback about Budgit.
          </Bullet>
          <Bullet>If we reschedule or end the call early, you still qualify.</Bullet>
          <Bullet>
            Calls you miss, leave early, or join without giving feedback don&apos;t qualify.
          </Bullet>
        </List>
        <Body>
          Any honest feedback counts. Your reward doesn&apos;t depend on whether it&apos;s positive
          or negative.
        </Body>
      </Section>

      <Section id="recording" title="Recording">
        <Body>
          We won&apos;t record or transcribe your call unless we ask at the start and you agree.
        </Body>
      </Section>

      <Section id="quotes" title="Quotes">
        <Body>
          We won&apos;t quote you publicly unless we ask first and you agree. If you agree, every
          public quote will carry this note: &ldquo;{REWARD_TERMS.quoteDisclosure}&rdquo;.
        </Body>
      </Section>

      <Section id="delivery" title="Delivery">
        <Body>
          We&apos;ll email your gift card to your Budgit account address within 14 days after your
          call. The card is subject to its issuer&apos;s terms. Budgit adds no expiration date or
          fees.
        </Body>
      </Section>

      <Section id="changes" title="Changes and fraud">
        <Body>
          We may end this offer early by updating this page and closing the booking page. Once 25
          calls are completed, a later booking does not get a gift card. We may withhold a reward
          for fraud, abuse, or ineligibility. Void where prohibited.
        </Body>
      </Section>

      <Section id="your-information" title="Your information">
        <Body>
          We use what you tell us to improve Budgit, as described in our{' '}
          <Link href="/privacy">Privacy Policy</Link>. We share your email address with our gift
          card provider only to deliver your card.
        </Body>
      </Section>

      <Section id="offered-by" title="Offered by">
        <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2, fontWeight: 600, whiteSpace: 'pre-line' }}>
          {`Budgit
11985 S. Monticello Terrace
Olathe, KS 66061`}
        </Typography>
        <Body>
          Questions? Contact us at <Link href="mailto:support@budgit.app">support@budgit.app</Link>.
        </Body>
      </Section>
    </Container>
  </Box>
);

export default FeedbackRewardTerms;
